/**
 * Custom hook for competitive-intel (CI) search via ``POST /api/ci/search``.
 *
 * Follows the same fetch/error/abort conventions as
 * the former SBA document search hook (token retrieval, impersonation headers,
 * ``AbortController`` per request), minus the SSE parsing — ``/api/ci/search``
 * is a plain one-shot JSON endpoint (the ``/search/stream`` twin exists for a
 * future streaming UI but isn't needed for the results-table experience
 * this hook backs).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import type { CIDocumentResult, CISearchResponse } from '../types';

const API_URL =
  import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export const CI_DOCUMENT_PAGE_SIZE = 20;

interface CiSearchState {
  /** Ranked CI documents — populated once the search resolves. */
  documents: CIDocumentResult[];
  /** True while the request is in flight. */
  isLoading: boolean;
  /** Error if the request failed. */
  error: Error | null;
  /** Total logical documents available across result pages. */
  totalDocuments: number;
  /** Final metadata from the response envelope. */
  metadata: CISearchResponse['metadata'] | null;
}

export function useCiSearch(
  query: string,
  enabled: boolean = true,
  page: number = 1,
  pageSize: number = CI_DOCUMENT_PAGE_SIZE,
): CiSearchState {
  const [documents, setDocuments] = useState<CIDocumentResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [metadata, setMetadata] = useState<CISearchResponse['metadata'] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback(async (
    searchQuery: string,
    requestedPage: number,
    requestedPageSize: number,
  ) => {
    // Abort any previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    // Clear the previous query's rows/total IMMEDIATELY (not after the new
    // response arrives) — otherwise a new query or a page change shows the
    // PREVIOUS query's cards under the new query's total, and CiResults'
    // loading-spinner branch (isLoading && documents.length === 0) never
    // fires on a re-query because the stale array is still non-empty.
    // Reset documents/document count right after setIsLoading(true).
    setDocuments([]);
    setTotalDocuments(0);
    setMetadata(null);

    try {
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken(false) : null;
      if (controller.signal.aborted) return;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      Object.assign(headers, getImpersonationHeaders());

      const response = await fetch(`${API_URL}/ci/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: searchQuery,
          page: requestedPage,
          page_size: requestedPageSize,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`CI search failed: ${response.status} ${response.statusText}`);
      }

      const data = (await response.json()) as CISearchResponse;
      if (controller.signal.aborted) return;

      setDocuments(data.documents);
      setTotalDocuments(data.total_documents);
      setMetadata(data.metadata ?? null);
      setIsLoading(false);
    } catch (err: unknown) {
      if (
        controller.signal.aborted
        || (err instanceof Error && err.name === 'AbortError')
      ) {
        return;
      }
      console.error('[CI-SEARCH] Request failed:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !query.trim()) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setDocuments([]);
      setIsLoading(false);
      setError(null);
      setTotalDocuments(0);
      setMetadata(null);
      return;
    }

    runSearch(query, page, pageSize);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, enabled, page, pageSize, runSearch]);

  return {
    documents,
    isLoading,
    error,
    totalDocuments,
    metadata,
  };
}

export default useCiSearch;
